import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSpinner, faTrash,
  faCheckCircle, faTimesCircle, faUserPlus,
  faShieldAlt, faBriefcase, faChartLine, faCreditCard, faTruckMoving
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchEntityAccess, grantAccess, revokeAccess, updateAccess
} from '../../../services/corporateStructureService';
import axiosInstance from '../../../utils/axiosConfig';
import { useFlashMessage } from '../../FlashMessage';
import './EntityAccessPanel.css';

const extractApiError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  if (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) {
    return data.non_field_errors[0];
  }
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (Array.isArray(value) && value.length > 0) {
      return `${key}: ${value[0]}`;
    }
    if (typeof value === 'string' && value) {
      return value;
    }
  }
  return fallback;
};

const ACCESS_FLAGS = [
  { key: 'has_operations_access', label: 'Ops', fullLabel: 'Operations', icon: faBriefcase, description: 'Trade & shipment operations' },
  { key: 'has_financial_access', label: 'Fin', fullLabel: 'Financial', icon: faCreditCard, description: 'Invoicing & financial data' },
  { key: 'has_crm_access', label: 'CRM', fullLabel: 'CRM', icon: faChartLine, description: 'Partner profiles & contacts' },
  { key: 'has_delivery_access', label: 'Del', fullLabel: 'Delivery', icon: faTruckMoving, description: 'Delivery management' },
];

const MEMBERSHIP_TYPES = [
  { value: 'full', label: 'Full Access', description: 'Complete read & write access to all modules' },
  { value: 'operations', label: 'Operations Only', description: 'Access limited to trade operations' },
  { value: 'finance', label: 'Finance Only', description: 'Access limited to financial modules' },
  { value: 'readonly', label: 'Read Only', description: 'View-only access, no modifications' },
];

const EntityAccessPanel = ({ entityId, entityName }) => {
  const { showSuccess, showError } = useFlashMessage();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [newGrant, setNewGrant] = useState({
    employee: '',
    membership_type: 'readonly',
    has_operations_access: false,
    has_financial_access: false,
    has_crm_access: false,
    has_delivery_access: false,
  });
  const [saving, setSaving] = useState(false);

  const loadAccess = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchEntityAccess(entityId);
      setGrants(data);
    } catch (err) {
      console.error('Failed to load access grants:', err);
      showError(extractApiError(err, 'Failed to load access grants.'));
    } finally {
      setLoading(false);
    }
  }, [entityId, showError]);

  const loadEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/corporate-structure/linkable/employees/');
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
      showError(extractApiError(err, 'Failed to load employees.'));
    }
  }, [showError]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const handleToggleFlag = async (grant, flagKey) => {
    try {
      await updateAccess(grant.id, { [flagKey]: !grant[flagKey] });
      loadAccess();
    } catch (err) {
      console.error('Failed to update access:', err);
      showError(extractApiError(err, 'Failed to update access.'));
    }
  };

  const handleRevoke = async (accessId) => {
    try {
      await revokeAccess(accessId);
      loadAccess();
    } catch (err) {
      console.error('Failed to revoke access:', err);
      showError(extractApiError(err, 'Failed to revoke access.'));
    }
  };

  const handleAddAccess = async (e) => {
    e.preventDefault();
    if (!newGrant.employee) return;
    try {
      setSaving(true);
      await grantAccess(entityId, newGrant);
      showSuccess(
        entityName
          ? `Access granted for ${entityName}.`
          : 'Access granted.'
      );
      setShowAddForm(false);
      setNewGrant({
        employee: '', membership_type: 'readonly',
        has_operations_access: false, has_financial_access: false,
        has_crm_access: false, has_delivery_access: false,
      });
      loadAccess();
    } catch (err) {
      console.error('Failed to grant access:', err);
      showError(extractApiError(err, 'Failed to grant access.'));
    } finally {
      setSaving(false);
    }
  };

  const handleShowAddForm = () => {
    if (!showAddForm) loadEmployees();
    setShowAddForm(!showAddForm);
  };

  if (loading) {
    return (
      <div className="eap-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  return (
    <div className="eap-container">
      <div className="eap-toolbar">
        <button className="sg-btn sg-btn-primary eap-toolbar-btn" onClick={handleShowAddForm}>
          <FontAwesomeIcon icon={faUserPlus} />
          Grant Access
        </button>
      </div>

      {showAddForm && (
        <form className="eap-add-form" onSubmit={handleAddAccess}>
          <div className="eap-form-header">
            <FontAwesomeIcon icon={faShieldAlt} className="eap-form-header-icon" />
            <div>
              <span className="eap-form-title">Grant Entity Access</span>
              <span className="eap-form-description">Select an employee and configure their permissions for this entity.</span>
            </div>
          </div>

          <div className="eap-field-group">
            <label className="eap-field-label">Employee</label>
            <select
              className="eap-select"
              value={newGrant.employee}
              onChange={e => setNewGrant({ ...newGrant, employee: e.target.value })}
            >
              <option value="">Choose an employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} ({emp.email})
                </option>
              ))}
            </select>
          </div>

          <div className="eap-field-group">
            <label className="eap-field-label">Access Level</label>
            <select
              className="eap-select"
              value={newGrant.membership_type}
              onChange={e => setNewGrant({ ...newGrant, membership_type: e.target.value })}
            >
              {MEMBERSHIP_TYPES.map(mt => (
                <option key={mt.value} value={mt.value}>{mt.label}</option>
              ))}
            </select>
            <span className="eap-field-hint">
              {MEMBERSHIP_TYPES.find(mt => mt.value === newGrant.membership_type)?.description}
            </span>
          </div>

          <div className="eap-field-group">
            <label className="eap-field-label">Module Permissions</label>
            <div className="eap-permission-grid">
              {ACCESS_FLAGS.map(flag => (
                <label
                  key={flag.key}
                  className={`eap-permission-card ${newGrant[flag.key] ? 'active' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={newGrant[flag.key]}
                    onChange={e => setNewGrant({ ...newGrant, [flag.key]: e.target.checked })}
                  />
                  <FontAwesomeIcon icon={flag.icon} className="eap-permission-icon" />
                  <span className="eap-permission-name">{flag.fullLabel}</span>
                  <span className="eap-permission-desc">{flag.description}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="eap-add-actions">
            <button type="button" className="sg-btn sg-btn-secondary eap-toolbar-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button type="submit" className="sg-btn sg-btn-primary eap-toolbar-btn" disabled={saving || !newGrant.employee}>
              <FontAwesomeIcon icon={saving ? faSpinner : faPlus} spin={saving} />
              {saving ? 'Granting...' : 'Grant Access'}
            </button>
          </div>
        </form>
      )}

      {grants.length === 0 ? (
        <div className="eap-empty">No access grants yet</div>
      ) : (
        <div className="eap-table">
          <div className="eap-table-header">
            <span className="eap-col-name">Employee</span>
            {ACCESS_FLAGS.map(flag => (
              <span key={flag.key} className="eap-col-flag" title={flag.fullLabel}>{flag.label}</span>
            ))}
            <span className="eap-col-action"></span>
          </div>
          {grants.map(grant => (
            <div key={grant.id} className="eap-table-row">
              <span className="eap-col-name" title={grant.employee_email}>
                {grant.employee_name}
                <span className="eap-role-badge">{grant.membership_type}</span>
              </span>
              {ACCESS_FLAGS.map(flag => (
                <span
                  key={flag.key}
                  className={`eap-col-flag eap-flag-toggle ${grant[flag.key] ? 'active' : ''}`}
                  onClick={() => handleToggleFlag(grant, flag.key)}
                  title={`Toggle ${flag.fullLabel}`}
                >
                  <FontAwesomeIcon icon={grant[flag.key] ? faCheckCircle : faTimesCircle} />
                </span>
              ))}
              <span className="eap-col-action">
                <button className="eap-revoke-btn" onClick={() => handleRevoke(grant.id)} title="Revoke access">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default EntityAccessPanel;
